<script>
import { cloneDeep } from 'lodash-es';
import { mapActions, mapState } from 'vuex';

import TwWysiwyg from '../../components/TwWysiwyg';
import ItineraryEditPosterColor from '../../components/itinerary/ItineraryEditPosterColor';
import ItineraryEditPosterFooter from '../../components/itinerary/ItineraryEditPosterFooter';
import ItineraryEditPosterImages from '../../components/itinerary/ItineraryEditPosterImages';
import ItineraryEditPosterIncluded from '../../components/itinerary/ItineraryEditPosterIncluded';
import ItineraryEditPosterInfo from '../../components/itinerary/ItineraryEditPosterInfo';

export default {
  name: 'ItineraryEditPoster',
  components: {
    TwWysiwyg,
    ItineraryEditPosterIncluded,
    ItineraryEditPosterImages,
    ItineraryEditPosterInfo,
    ItineraryEditPosterFooter,
    ItineraryEditPosterColor,
  },
  data() {
    return {
      itineraryId: null,
      domainId: null,
      localItineraryDomainPoster: null,
      validation: {
        description: null,
        flight: null,
      },
    };
  },
  computed: {
    ...mapState({
      api: state => state.app.twenty.urls.api,
      itinerary: state => state.itinerary.edit.item,
      poster: state => state.itinerary.poster.item,
      loading: state => state.itinerary.poster.loading,
      error: state => state.itinerary.poster.error,
      selectedDomain: state => state.itinerary.domains.selected,
    }),
  },
  watch: {
    poster(newVal) {
      if (newVal) {
        this.localItineraryDomainPoster = cloneDeep(newVal);
      }
    },
    selectedDomain(newVal) {
      if (newVal) {
        this.getItineraryDomainPoster({
          itineraryId: this.itineraryId,
          domainId: this.selectedDomain.domainId,
        }).then(() => {
          if (this.poster) {
            this.localItineraryDomainPoster = cloneDeep(this.poster);
          }
        });
      }
    },
  },
  created() {
    this.itineraryId = parseInt(this.$route.params.id, 10);
  },
  methods: {
    ...mapActions({
      getItineraryDomainPoster: 'itinerary/getItineraryDomainPoster',
      createItineraryDomainPoster: 'itinerary/createItineraryDomainPoster',
      updateItineraryDomainPoster: 'itinerary/updateItineraryDomainPoster',
      generateItineraryDomainPosterQrCode: 'itinerary/generateItineraryDomainPosterQrCode',
    }),
    saveItineraryDomainPoster() {
      this.updateItineraryDomainPoster({
        itineraryId: this.itineraryId,
        domainId: this.selectedDomain.domainId,
        data: this.localItineraryDomainPoster,
      });
    },
    updateField(value, field, section) {
      if (section && field) {
        if (Object.prototype.hasOwnProperty.call(this.localItineraryDomainPoster[section], field)) {
          this.localItineraryDomainPoster[section][field] = value;
        }
      } else if (field) {
        if (Object.prototype.hasOwnProperty.call(this.localItineraryDomainPoster, field)) {
          this.localItineraryDomainPoster[field] = value;
        }
      }
    },
    validateField(value, field, section) {
      switch (field) {
        case 'description':
          if (value.length > 1000) {
            this.validation.description = 'Description exceeds the maximum character limit of 1000.';
          } else {
            this.validation.description = null;
            this.updateField(value, 'description', section);
          }
          break;
        case 'flight':
          if (value.length > 255) {
            this.validation.flight = 'Flight exceeds the maximum character limit of 255.';
          } else {
            this.validation.flight = null;
            this.updateField(value, 'flight', section);
          }
          break;
        default: this.updateField(value, field, section);
      }
    },
    generateQrCode(event) {
      this.generateItineraryDomainPosterQrCode({
        itineraryId: this.itineraryId,
        domainId: this.selectedDomain.domainId,
        data: event,
      });
      this.updateItineraryDomainPoster({
        itineraryId: this.itineraryId,
        domainId: this.selectedDomain.domainId,
        data: this.localItineraryDomainPoster,
      });
    },
    getPdfLink() {
      return `${this.api}products/itinerary/${this.itineraryId}/domain/${this.selectedDomain.domainId}/poster/pdf`;
    },
  },
};
</script>

<template>
  <div>
    <v-layout v-if="!selectedDomain">
      <div class="pt-4 w-100 d-flex flex-col gap-3">
        <v-card>
          <v-card-title class="pb-0">
            <div class="headline">Select a domain</div>
          </v-card-title>
          <v-card-text>
            <p>Please select a domain to create a poster.</p>
          </v-card-text>
        </v-card>
      </div>
    </v-layout>

    <v-layout v-else-if="selectedDomain && (!poster || poster.length === 0)">
      <div class="pt-4 w-100 d-flex flex-col gap-3">

        <v-card>
          <v-card-title class="pb-0">
            <div class="headline">Selected domain: {{ selectedDomain.domain.shortName }}</div>
          </v-card-title>
          <v-card-text>
            <v-btn
              color="success"
              @click="createItineraryDomainPoster({ itineraryId, domainId: selectedDomain.domainId })"
            >
              Create Poster
            </v-btn>
          </v-card-text>
        </v-card>
      </div>
    </v-layout>

    <v-layout v-else-if="localItineraryDomainPoster">
      <div class="pt-4 w-100 d-flex flex-col gap-3">
        <v-card>
          <v-card-title class="pb-0">
            <div class="headline">Title</div>
          </v-card-title>
          <v-card-text>
            <v-text-field
              :loading="loading"
              :value="localItineraryDomainPoster.title"
              label="
                Poster title. Default the same as Itinerary Local name.
                Overwrite here if you want to use an alternative title."
              @input="updateField($event,'title',null,)"
            />
          </v-card-text>
        </v-card>

        <v-card class="h-100">
          <v-card-title class="pb-0">
            <div class="headline">Subtitle</div>
          </v-card-title>
          <v-card-text>
            <div class="d-flex flex-col">
              <p class="mb-0"/>
              <v-text-field
                :loading="loading"
                :value="localItineraryDomainPoster.subTitle"
                label="From offer info / day program or overwrite name here."
                @input="updateField($event,'subTitle',null )"
              />
            </div>
          </v-card-text>
        </v-card>

        <v-card class="h-100">
          <v-card-title class="pb-0">
            <div class="headline">Colors</div>
          </v-card-title>
          <v-card-text>
            <div class="d-flex flex-col gap-3">
              <itinerary-edit-poster-color
                :value="localItineraryDomainPoster.backgroundColor"
                :disabled="loading"
                label="Background color"
                @input="updateField($event,'backgroundColor',null)"
              />
              <itinerary-edit-poster-color
                :value="localItineraryDomainPoster.textColor"
                :disabled="loading"
                label="Text color"
                @input="updateField($event,'textColor',null,)"
              />
              <itinerary-edit-poster-color
                :value="localItineraryDomainPoster.logoColor"
                :disabled="loading"
                label="Logo color"
                @input="updateField($event,'logoColor',null)"
              />
              <itinerary-edit-poster-color
                :value="localItineraryDomainPoster.linkColor"
                :disabled="loading"
                label="Link color"
                @input="updateField($event,'linkColor',null )"
              />
            </div>
          </v-card-text>
        </v-card>

        <div class="d-flex flex-row gap-3 w-100">
          <div class="w-50 h-100 d-flex flex-col gap-3">
            <v-card class="h-50">
              <v-card-title class="pb-0">
                <div class="headline">Description</div>
              </v-card-title>
              <v-card-text>
                <p>For optimal readability, we recommend keeping the text length between 900 and 1000 characters.</p>
                <tw-wysiwyg
                  :value="localItineraryDomainPoster.description"
                  :placeholder="$t('itinerary.edit.dayProgram.table.text')"
                  :disabled="loading"
                  @change="validateField($event,'description',null)"
                />
                <p v-if="validation.description" class="error-message"> {{ validation.description }} </p>
              </v-card-text>
            </v-card>

            <v-card class="h-50">
              <v-card-title class="pb-0">
                <div class="headline">Flight</div>
              </v-card-title>
              <v-card-text>
                <tw-wysiwyg
                  :value="localItineraryDomainPoster.flight"
                  :placeholder="$t('itinerary.edit.dayProgram.table.text')"
                  :disabled="loading"
                  @change="validateField($event,'flight',null)"
                />
                <p v-if="validation.flight" class="error-message"> {{ validation.flight }} </p>
              </v-card-text>
            </v-card>
          </div>
          <div class="w-50 h-100">
            <itinerary-edit-poster-included
              :itinerary-poster="localItineraryDomainPoster"
              @save="saveItineraryDomainPoster"
            />
          </div>
        </div>

        <v-card>
          <v-card-title class="pb-0">
            <div class="headline"><v-icon>image</v-icon> Select 4 images</div>
          </v-card-title>
          <v-card-text>
            <itinerary-edit-poster-images
              :max-images="4"
            />
          </v-card-text>
        </v-card>

        <div class="d-flex flex-row gap-3 w-100">
          <div class="w-100">
            <itinerary-edit-poster-info
              :itinerary-poster="localItineraryDomainPoster"
              @save="saveItineraryDomainPoster"
              @generate-qr-code="generateQrCode($event)"
            />
          </div>
        </div>

        <itinerary-edit-poster-footer :itinerary-poster="localItineraryDomainPoster" @update-field="updateField" />

        <div>
          <v-btn :loading="loading" color="primary" @click="saveItineraryDomainPoster">
            {{ $t('buttons.save') }}
            <v-icon class="ml-2">save</v-icon>
          </v-btn>

          <a :href="getPdfLink()" target="_blank">
            <v-btn :loading="loading" color="secondary" type="button">
              {{ $t('buttons.preview') }}
              <v-icon class="ml-2">book</v-icon>
            </v-btn>
          </a>
        </div>

      </div>
    </v-layout>
  </div>
</template>

<style lang="scss">
.error-message {
  color: #ff5252;
  margin-top: 14px;
}
.w-25 {
  width: 25% !important;
}

.w-50 {
  width: 50% !important;
}

.w-75 {
  width: 75% !important;
}

.w-100 {
  width: 100% !important;
}

.h-100 {
  height: 100% !important;
}

h-50 {
  height: 50% !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/itinerary/ItineraryEditPoster.vue